import React, { useEffect, useState } from "react";
import PageTableLayout from "../../components/Pagetablelayout";
import axios from "axios";
import { Space, Button, Modal, Form, Input, Select, Switch, Row, Col, Popconfirm } from "antd";

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]); // for tags multi-select
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [form] = Form.useForm();

  const token = localStorage.getItem("jwt");
  const API_BASE = process.env.REACT_APP_API_URL || "https://mdgholidaydreams.com/api";

  const headersWithAuth = (extra = {}) =>
    token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };

  // Helper: try parse JSON array or fallback to comma split
  function tryParseJsonArray(value) {
    if (value === null || value === undefined || value === "") return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "object") return Array.isArray(value) ? value : [];
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : String(value).split(",").map((s) => s.trim()).filter(Boolean);
    } catch {
      return String(value).split(",").map((s) => s.trim()).filter(Boolean);
    }
  }

  const fetchTagsOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tags/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      const opts = (data || []).map((t) => ({
        label: t.name,
        value: t.name, // change to t.id if backend expects ids
      }));
      setTagsOptions(opts);
    } catch (err) {
      console.error("Fetch tags options failed:", err.response?.data ?? err);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE}/countries/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setCountries((data || []).map((c) => ({ ...c, id: c.id, name: c.name })));
    } catch (err) {
      console.error("Fetch countries failed:", err.response?.data ?? err);
    }
  };

  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/cities/`, {
        headers: headersWithAuth(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      const parsed = (data || []).map((c) => ({
        ...c,
        offerings: tryParseJsonArray(c.offerings),
        tags: tryParseJsonArray(c.tags ?? c.tag), // support both keys
        active: Number(c.active),
        featured_on_homepage: Number(c.featured_on_homepage) === 1,
        featured_destination: Number(c.featured_destination) === 1,
      }));
      setCities(parsed);
    } catch (err) {
      console.error("Fetch cities failed:", err.response?.data ?? err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTagsOptions();
    fetchCountries();
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    setEditingCity(null);
    form.resetFields();
    form.setFieldsValue({
      active: 1,
      featured_on_homepage: false,
      featured_destination: false,
      offerings: [],
      tags: [],
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCity(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      country_id: record.country_id,
      active: Number(record.active),
      image_url: record.image_url,
      caption: record.caption,
      color: record.color,
      featured_on_homepage: !!record.featured_on_homepage,
      featured_destination: !!record.featured_destination,
      offerings: tryParseJsonArray(record.offerings),
      tags: tryParseJsonArray(record.tags ?? record.tag),
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Normalize arrays and booleans
      const offerings = Array.isArray(values.offerings) ? values.offerings : tryParseJsonArray(values.offerings);
      const tags = Array.isArray(values.tags) ? values.tags : tryParseJsonArray(values.tags);
      const payload = {
        name: values.name,
        code: values.code,
        country_id: values.country_id,
        tags: tags, // array of tag names or ids depending on backend
        description: values.description ?? null,
        image_url: values.image_url ?? null,
        offerings: offerings,
        best_time: values.best_time ?? null,
        currency: values.currency ?? null,
        capital: values.capital ?? null,
        caption: values.caption ?? null,
        color: values.color ?? null,
        featured_on_homepage: values.featured_on_homepage ? 1 : 0,
        featured_destination: values.featured_destination ? 1 : 0,
        note: values.note ?? null,
        active: Number(values.active ?? 1),
      };

      console.log("City payload being sent:", payload);

      if (editingCity) {
        // Use RESTful id path with trailing slash to avoid redirect that drops body
        await axios.put(`${API_BASE}/cities/${editingCity.id}/`, payload, {
          headers: headersWithAuth({ "Content-Type": "application/json" }),
        });
      } else {
        await axios.post(`${API_BASE}/cities/`, payload, {
          headers: headersWithAuth({ "Content-Type": "application/json" }),
        });
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingCity(null);
      fetchCities();
    } catch (err) {
      console.error("Save city failed:", err.response?.data ?? err);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCity(null);
    form.resetFields();
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Code", dataIndex: "code", key: "code" },
    { title: "Country", dataIndex: "country_name", key: "country_name" },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (t) =>
        Array.isArray(t)
          ? t.map((item, i) => (
              <span
                key={i}
                style={{
                  marginRight: 6,
                  background: "#e6f7ff",
                  padding: "2px 6px",
                  borderRadius: 4,
                  display: "inline-block",
                }}
              >
                {item}
              </span>
            ))
          : "",
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) => (Number(active) === 1 ? "Active" : "Inactive"),
    },
    { title: "Created By", dataIndex: "created_by", key: "created_by" },
    { title: "Updated By", dataIndex: "updated_by", key: "updated_by" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>

          <Popconfirm
            title="Deactivate this city?"
            okText="Yes"
            cancelText="No"
            onConfirm={async () => {
              try {
                await axios.patch(`${API_BASE}/cities/${record.id}/`, {}, {
                  headers: headersWithAuth(),
                });
                fetchCities();
              } catch (err) {
                console.error("Deactivate failed:", err.response?.data ?? err);
              }
            }}
          >
            <Button danger>Deactivate</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTableLayout
        breadcrumb={["Admin", "Cities"]}
        title="Manage Cities"
        addButtonLabel="Add City"
        onAdd={handleAdd}
        columns={columns}
        dataSource={cities}
        loading={loading}
        onRefresh={fetchCities}
      />

      <Modal
        title={editingCity ? "Edit City" : "Add New City"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            active: 1,
            featured_on_homepage: false,
            featured_destination: false,
            offerings: [],
            tags: [],
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="City Name" rules={[{ required: true, message: "Please enter a city name" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label="City Code" rules={[{ required: true, message: "Please enter a city code" }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="country_id" label="Country" rules={[{ required: true, message: "Please select a country" }]}>
                <Select placeholder="Select country">
                  {countries.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="active" label="Status" rules={[{ required: true, message: "Please select status" }]}>
                <Select placeholder="Select status">
                  <Select.Option value={1}>Active</Select.Option>
                  <Select.Option value={0}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Tags and offerings */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tags" label="Tags">
                <Select
                  mode="multiple"
                  options={tagsOptions}
                  style={{ width: "100%" }}
                  placeholder="Select or type tags"
                  tokenSeparators={[","]}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="offerings" label="Offerings">
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder="Enter offerings (press Enter after each)"
                  tokenSeparators={[","]}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Homepage slider fields */}
          <Row gutter={16} style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 8 }}>
            <Col span={24}>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8, fontWeight: 600, letterSpacing: 0.5 }}>
                HOMEPAGE SLIDER (shown on the public site's hero banner)
              </div>
            </Col>

            <Col span={24}>
              <Form.Item name="image_url" label="Slider Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="caption" label="Slider Caption">
                <Input placeholder="e.g. Where turquoise waters kiss powder-white sands" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="color"
                label="Accent Color"
                rules={[{ pattern: /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, message: "Use a hex color like #1A9ED4" }]}
              >
                <Input placeholder="#1A9ED4" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="featured_on_homepage" label="Show in Homepage Slider" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* Explore destinations grid */}
          <Row gutter={16} style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 8 }}>
            <Col span={24}>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8, fontWeight: 600, letterSpacing: 0.5 }}>
                EXPLORE THE ISLANDS (shown as a destination card on the homepage, max 3 displayed)
              </div>
            </Col>

            <Col span={24}>
              <Form.Item name="featured_destination" label="Show in 'Explore the Islands'" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
